import type { BlendIf, BlendIfChannel, BlendIfRange } from './types';
import { DEFAULT_BLEND_IF, DEFAULT_BLEND_IF_RANGE } from './types';

function clampByte(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(255, Math.max(0, Math.round(value)));
}

export function cloneBlendIfRange(range: BlendIfRange = DEFAULT_BLEND_IF_RANGE): BlendIfRange {
  let black0 = clampByte(range.black0);
  let black1 = clampByte(range.black1);
  let white0 = clampByte(range.white0);
  let white1 = clampByte(range.white1);
  if (black1 < black0) [black0, black1] = [black1, black0];
  if (white1 < white0) [white0, white1] = [white1, white0];
  if (white0 < black1) {
    const mid = Math.round((black1 + white0) / 2);
    black1 = Math.min(black1, mid);
    white0 = Math.max(white0, mid);
    if (white1 < white0) white1 = white0;
  }
  return { black0, black1, white0, white1 };
}

export function cloneBlendIf(value: BlendIf | null | undefined): BlendIf {
  if (!value) {
    return {
      channel: DEFAULT_BLEND_IF.channel,
      thisLayer: { ...DEFAULT_BLEND_IF_RANGE },
      underlying: { ...DEFAULT_BLEND_IF_RANGE },
    };
  }
  const channel: BlendIfChannel =
    value.channel === 'red' || value.channel === 'green' || value.channel === 'blue'
      ? value.channel
      : 'gray';
  return {
    channel,
    thisLayer: cloneBlendIfRange(value.thisLayer),
    underlying: cloneBlendIfRange(value.underlying),
  };
}

export function blendIfRangeIsDefault(range: BlendIfRange): boolean {
  return (
    range.black0 === 0 &&
    range.black1 === 0 &&
    range.white0 === 255 &&
    range.white1 === 255
  );
}

export function blendIfIsDefault(value: BlendIf | null | undefined): boolean {
  const blendIf = cloneBlendIf(value);
  return (
    blendIf.channel === 'gray' &&
    blendIfRangeIsDefault(blendIf.thisLayer) &&
    blendIfRangeIsDefault(blendIf.underlying)
  );
}

/** Pack a range as 0..1 for the composite shader: black0, black1, white0, white1. */
export function blendIfRangeToUniform(range: BlendIfRange): [number, number, number, number] {
  const r = cloneBlendIfRange(range);
  return [r.black0 / 255, r.black1 / 255, r.white0 / 255, r.white1 / 255];
}

export function blendIfChannelIndex(channel: BlendIfChannel): number {
  if (channel === 'red') return 1;
  if (channel === 'green') return 2;
  if (channel === 'blue') return 3;
  return 0;
}
